/**
 * aiAuditLog.js
 * --------------------------------------------------------------------------
 * EU AI Act Article 12 (Record-keeping) compliance service.
 *
 * Article 12 requires high-risk AI systems to "automatically record events
 * over the lifetime of the system" so that decisions made by the AI can
 * be reconstructed, audited, and challenged if necessary. For ClearEqual
 * this means logging every CV parse and every skill-match calculation.
 *
 * The log lives in localStorage for the demo. In production this is a
 * server-side append-only log with cryptographic integrity (e.g. signed
 * with HMAC and shipped to immutable storage like AWS QLDB or a dedicated
 * audit database).
 *
 * Each entry captures enough information to reconstruct the decision:
 *   - timestamp
 *   - operation type (parse | match)
 *   - subject identifier (employee ID, hashed in production)
 *   - input metadata (filename, file size, role compared)
 *   - output (skills detected, score, model version)
 *   - human oversight flag (was a human required to review this?)
 */

const STORAGE_KEY = 'clearequal.aiAuditLog.v1';
const MAX_ENTRIES = 500; // ring buffer cap so localStorage doesn't grow forever

function readLog() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function writeLog(entries) {
  try {
    const trimmed = entries.slice(-MAX_ENTRIES);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(trimmed));
  } catch {
    // Quota or serialisation error, fail silently, never block the user
  }
}

/**
 * Log an event. The `operation` is one of:
 *   - 'cv_parse'         : CV uploaded and parsed
 *   - 'skill_match'      : Profile scored against a role
 *   - 'ai_insight'       : Claude AI generated an insight
 *   - 'role_change'      : User selected a different comparison role
 */
export function logAiEvent(event) {
  const enriched = {
    ...event,
    timestamp: new Date().toISOString(),
    appVersion: '6.2.28',
    // Article 14 (Human Oversight), flag if this output should not be
    // acted upon without a human reviewer
    humanOversightRequired: ['skill_match', 'ai_insight'].includes(event.operation),
  };
  const log = readLog();
  log.push(enriched);
  writeLog(log);
  return enriched;
}

/**
 * Read the full log. Used by the AI Act compliance panel to surface
 * recent AI activity for audit / human-oversight purposes.
 */
export function getAuditLog({ limit = 50, operation = null } = {}) {
  const log = readLog();
  let filtered = log;
  if (operation) {
    filtered = filtered.filter((e) => e.operation === operation);
  }
  return filtered.slice(-limit).reverse();
}

/**
 * Clear the log. Reserved for the data subject's right to erasure (GDPR
 * Art. 17). Never called from the AI side.
 */
export function clearAuditLog() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {}
}
